<template>
  <EasyDataTable
      :empty-message="t('global.table.noData')"
      :filter-options="filterOptions"
      :headers="headers"
      :hide-footer="isSoloItem"
      :hide-rows-per-page="true"
      :items="items"
      :loading="isLoading"
      :rows-of-page-separator-message="t('global.table.of')"
      :rows-per-page="isSoloItem ? 1 : 10"
      :search-value="searchValue"
      :table-min-height="0"
      alternating
      buttons-pagination
      header-item-class-name="vue3-easy-data-table__header-item"
      theme-color="#00ADEE"
  >
    <template v-for="header in headersWithTooltip" :key="header.value" #[`item-${header.value}`]="item">
      <span v-if="item[header.tooltip]" v-tippy="item[header.tooltip]">{{ item[header.value] }}</span>
      <span v-else>{{ item[header.value] }}</span>
    </template>
    <template #item-status="item">
      <div class="tag">
        <p>{{ item.status }}</p>
      </div>
    </template>
    <template #item-category="item">
      <div v-if="item.category" :class="['tag', item.categoryColor]">
        <p>{{ item.category }}</p>
      </div>
    </template>
    <template #item-actions="item">
      <p v-if="item && item.actions" class="vue3-easy-data-table__actions">
        <router-link
            v-if="item.actions.view"
            v-tippy="t(`global.actions.view`)"
            :to="item.actions.view"
            class="vue3-easy-data-table__action"
        >
          <IconView class="icon icon--black"/>
        </router-link>
        <a
            v-if="item.actions.download"
            v-tippy="t(`global.actions.download`)"
            :href="item.actions.download"
            class="vue3-easy-data-table__action"
            target="_blank"
        >
          <IconDownload class="icon icon--black"/>
        </a>
        <button
            v-if="item.actions.favorite && item.id"
            v-tippy="item.actions.favorite.isInIt ? t('global.actions.removeFavorite') : t('global.actions.addFavorite')"
            class="vue3-easy-data-table__action"
            type="button"
        >
          <IconHeartFull
              v-if="item.actions.favorite.isInIt"
              class="icon icon--black"
              @click="handleRemoveFavorite(item.id)"
          />
          <IconHeartEmpty
              v-else
              class="icon icon--black"
              @click="handleAddFavorite(item.id)"
          />
        </button>
        <router-link
            v-if="item.actions.update"
            v-tippy="t(`global.actions.update`)"
            :to="item.actions.update"
            class="vue3-easy-data-table__action"
        >
          <IconEdit class="icon icon--black"/>
        </router-link>
        <router-link
            v-if="item.actions.recurrences"
            v-tippy="t(`global.actions.recurrences`)"
            :to="item.actions.recurrences"
            class="vue3-easy-data-table__action"
        >
          <IconCalendar class="icon icon--black"/>
        </router-link>
        <router-link
            v-if="item.actions.participants"
            v-tippy="t(`global.actions.participants`)"
            :to="item.actions.participants"
            class="vue3-easy-data-table__action"
        >
          <IconParticipants class="icon icon--black"/>
        </router-link>
        <router-link
            v-if="item.actions.registrations"
            v-tippy="t(`global.actions.registrations`)"
            :to="item.actions.registrations"
            class="vue3-easy-data-table__action"
        >
          <IconClipboard class="icon icon--black"/>
        </router-link>
        <button
            v-if="item.actions.edit && item.id"
            v-tippy="t(`global.actions.update`)"
            class="vue3-easy-data-table__action"
            type="button"
            @click="handleEdit(item.id)"
        >
          <IconEdit class="icon icon--black"/>
        </button>
        <router-link
          v-if="item.actions.transferParticipant && item.id"
          v-tippy="t(`global.actions.transferParticipant`)"
          :to="item.actions.transferParticipant"
          class="vue3-easy-data-table__action"
        >
          <IconTransfer class="icon icon--black"/>
        </router-link>
        <router-link
            v-if="item.actions.transferRegistration && item.id"
            v-tippy="t(`global.actions.transferRegistration`)"
            :to="item.actions.transferRegistration"
            class="vue3-easy-data-table__action"
        >
          <IconTransfer class="icon icon--black"/>
        </router-link>
        <a
            v-if="item.actions.bill"
            v-tippy="t(`global.actions.bill`)"
            :href=item.actions.bill
            class="vue3-easy-data-table__action"
            target="blank"
        >
          <IconExternal class="icon icon--black"/>
        </a>
        <button v-if="item.actions.resendUserInvite && item.userId"
                v-tippy="t(`global.actions.resendUserInvite`)"
                class="vue3-easy-data-table__action"
                type="button"
                @click="resendUserInvite(item.userId)"
        >
          <IconMail class="icon icon--black"/>
        </button>
        <button
            v-if="item.actions.duplicate && item.id"
            v-tippy="t(`global.actions.duplicate`)"
            class="vue3-easy-data-table__action"
            type="button"
            @click="handleDuplicate(item)"
        >
            <IconDuplicate class="icon icon--black"/>
        </button>
        <button
            v-if="item.actions.delete && item.id"
            v-tippy="t(`global.actions.delete`)"
            class="vue3-easy-data-table__action red-bg"
            type="button"
            @click="handleDelete(item)"
        >
          <IconDelete class="icon icon--black"/>
        </button>
      </p>
    </template>

  </EasyDataTable>
</template>

<script lang="ts" setup>
import type {FilterOption, Header, Item} from "vue3-easy-data-table"
import {computed} from "vue"
import {useI18n} from "vue3-i18n"
import IconCalendar from "@/assets/icons/icon__calendar.svg"
import IconClipboard from "@/assets/icons/icon__clipboard.svg"
import IconParticipants from "@/assets/icons/icon__person.svg"
import IconEdit from "@/assets/icons/icon__edit.svg"
import IconMail from "@/assets/icons/icon__mail.svg"
import IconDuplicate from "@/assets/icons/icon__copy.svg"
import IconDelete from "@/assets/icons/icon__delete.svg"
import IconDownload from "@/assets/icons/icon__download.svg"
import IconTransfer from "@/assets/icons/icon__transfer.svg"
import IconView from "@/assets/icons/icon__view.svg"
import IconHeartFull from "@/assets/icons/icon__heart--full.svg"
import IconHeartEmpty from "@/assets/icons/icon__heart--empty.svg"
import IconExternal from "@/assets/icons/icon__external.svg"

const {t} = useI18n()

// eslint-disable-next-line
const props = defineProps<{
  headers: (Header & { tooltip?: string })[],
  items: Item[],
  filterOptions?: FilterOption[],
  isLoading?: boolean,
  searchValue?: string
  isSoloItem?: boolean
}>()

const headersWithTooltip = computed(() =>
    props.headers.filter(h => h.tooltip)
)

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "delete", item: any): void
  (event: "resendUserInvite", userId: string): void
  (event: "edit", id: string): void
  (event: "addFavorite", id: string): void
  (event: "removeFavorite", id: string): void
  (event: "duplicate", item: any): void
}>()

function handleDuplicate(item: any) {
  emit("duplicate", item)
}

function handleDelete(item: any) {
  emit("delete", item)
}

function handleEdit(id: string) {
  emit("edit", id)
}

function resendUserInvite(userId: string) {
  emit("resendUserInvite", userId)
}

function handleAddFavorite(id: string) {
  emit("addFavorite", id)
}

function handleRemoveFavorite(id: string) {
  emit("removeFavorite", id)
}
</script>
